import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';

// Create a QueryClient with optimized configuration for AWS cost savings
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 30 minutes - prevent annoying reloads
      staleTime: 1000 * 60 * 30,
      // Keep unused data in cache for 1 hour
      gcTime: 1000 * 60 * 60,
      // Retry failed requests 1 time only
      retry: 1,
      // Don't refetch on window focus - prevents annoying reloads
      refetchOnWindowFocus: false,
      // Don't refetch on mount if data is fresh - prevents annoying reloads
      refetchOnMount: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onError: (error: any) => {
      // Silent in production - no console logs
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Something went wrong';
      toast.error(errorMessage);
    },
  }),
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Auth
  auth: {
    me: ['auth', 'me'] as const,
  },
  
  // Admin
  admin: {
    all: ['admin'] as const,
    users: (filters?: any) => ['admin', 'users', filters] as const,
    dashboardStats: ['admin', 'dashboardStats'] as const,
    reportedUsers: (filters?: any) => ['admin', 'reportedUsers', filters] as const,
    pendingBlogs: ['admin', 'pendingBlogs'] as const,
    
    // Commissions
    commissions: {
      all: ['admin', 'commissions'] as const,
      transactions: ['admin', 'commissions', 'transactions'] as const,
      marketingUsers: ['admin', 'commissions', 'marketingUsers'] as const,
      settings: ['admin', 'commissions', 'settings'] as const,
    },
    
    // Profile Edit Approval
    profileEditRequests: (filters?: any) => ['admin', 'profileEditRequests', filters] as const,
  },
  
  // Coupons
  coupons: {
    all: ['coupons'] as const,
    list: (filters?: any) => ['coupons', 'list', filters] as const,
    myCoupons: ['coupons', 'my'] as const,
  },
  
  // Plans
  plans: {
    all: ['plans'] as const,
    list: ['plans', 'list'] as const,
    detail: (id: string) => ['plans', 'detail', id] as const,
  },
  
  // Profiles
  profiles: {
    all: ['profiles'] as const,
    matches: (filters?: any) => ['profiles', 'matches', filters] as const,
    detail: (id: string) => ['profiles', 'detail', id] as const,
    me: ['profiles', 'me'] as const,
    shortlisted: ['profiles', 'shortlisted'] as const,
  },
  
  // Chats
  chats: {
    all: ['chats'] as const,
    list: ['chats', 'list'] as const,
    messages: (chatId: string) => ['chats', 'messages', chatId] as const,
  },
  
  // Blogs
  blogs: {
    all: ['blogs'] as const,
    list: (filters?: any) => ['blogs', 'list', filters] as const,
    detail: (slug: string) => ['blogs', 'detail', slug] as const,
  },
  
  // Settings
  settings: {
    all: ['settings'] as const,
    general: ['settings', 'general'] as const,
    discovery: ['settings', 'discovery'] as const,
    profileBooster: ['settings', 'profileBooster'] as const,
  },
  
  // Marketing
  marketing: {
    all: ['marketing'] as const,
    stats: (filters?: any) => ['marketing', 'stats', filters] as const,
    payouts: ['marketing', 'payouts'] as const,
  },
  
  // Notifications
  notifications: {
    all: ['notifications'] as const,
    list: ['notifications', 'list'] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
  },
  
  // Interactions
  interactions: {
    all: ['interactions'] as const,
    received: (filters?: any) => ['interactions', 'received', filters] as const,
    sent: (filters?: any) => ['interactions', 'sent', filters] as const,
  },
  
  // Users
  users: {
    all: ['users'] as const,
    me: ['users', 'me'] as const,
  },
  
  // Referral & Wallet
  wallet: {
    all: ['wallet'] as const,
    statistics: ['wallet', 'statistics'] as const,
    transactions: (limit?: number) => ['wallet', 'transactions', limit] as const,
  },
  
  referral: {
    all: ['referral'] as const,
    myReferrals: ['referral', 'my-referrals'] as const,
    statistics: ['referral', 'statistics'] as const,
    rewards: ['referral', 'rewards'] as const,
    settings: ['referral', 'settings'] as const,
    publicInfo: ['referral', 'public-info'] as const,
  },
  
  // Payout
  payout: {
    all: ['payout'] as const,
    myRequests: (limit?: number) => ['payout', 'my-requests', limit] as const,
  },
  
  // Subscriptions
  subscriptions: {
    all: ['subscriptions'] as const,
    plans: ['subscriptions', 'plans'] as const,
    mySubscription: ['subscriptions', 'my-subscription'] as const,
  },
  
  // Payments
  payments: {
    all: ['payments'] as const,
    history: ['payments', 'history'] as const,
    myPayments: ['payments', 'my-payments'] as const,
  },
  
  // Premium Plans
  premiumPlans: {
    all: ['premium-plans'] as const,
    list: ['premium-plans', 'list'] as const,
  },
  
  // Community
  community: {
    all: ['community'] as const,
    posts: (category?: string) => ['community', 'posts', category] as const,
    myPosts: ['community', 'my-posts'] as const,
  },
  
  // Activities
  activities: {
    all: ['activities'] as const,
    recent: (limit?: number) => ['activities', 'recent', limit] as const,
  },
  
  // Server
  server: {
    health: ['server', 'health'] as const,
  },
};